import {
  ActionButton,
  Button,
  Checkbox,
  CustomDialog,
  DialogContainer,
  TextArea,
} from '@react-spectrum/s2'
import {
  Check,
  Copy,
  Loader2,
  Printer,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { submitMessageFeedback } from '@rpc/chat.rpc'
import {
  MESSAGE_FEEDBACK_NEGATIVE_REASONS,
  MESSAGE_FEEDBACK_POSITIVE_REASONS,
} from '@shared/chat-types'
import type {
  MessageFeedbackReasonTag,
  MessageFeedbackRating,
  MessageFeedbackSummary,
} from '@shared/chat-types'
import { trackAnalyticsEvent } from '@client/analytics'
import { useKeyboardViewportInset } from '@/hooks/use-keyboard-viewport-inset'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'

type MessageFeedbackControlProps = {
  messageId: string
  markdownContent: string
  previousUserMessage?: string | null
  conversationUrl?: string | null
  timestampLabel?: string | null
  initialFeedback?: MessageFeedbackSummary | null
}

type PrintAnswerInput = {
  answerMarkdown: string
  previousUserMessage: string
  conversationUrl?: string | null
  timestampLabel?: string | null
}

function getReasonOptions(rating: MessageFeedbackRating | null) {
  return rating === 'negative'
    ? MESSAGE_FEEDBACK_NEGATIVE_REASONS
    : MESSAGE_FEEDBACK_POSITIVE_REASONS
}

function getPrintDocumentShell() {
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>BuildingRegs.app answer</title>
  <style>
    @page { margin: 18mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      color: #151515;
      background: #fff;
      font-family: "Google Sans", "Helvetica Neue", Arial, sans-serif;
      font-size: 13px;
      line-height: 1.5;
    }
    main {
      max-width: 760px;
      margin: 0 auto;
    }
    header {
      border-bottom: 1px solid #d9d9d9;
      padding-bottom: 14px;
      margin-bottom: 24px;
    }
    .brand {
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.01em;
      margin-bottom: 8px;
    }
    .meta {
      color: #555;
      font-size: 11px;
      overflow-wrap: anywhere;
    }
    .meta + .meta { margin-top: 3px; }
    section + section { margin-top: 24px; }
    h1, h2, h3, h4 {
      color: #111;
      line-height: 1.25;
      break-after: avoid;
    }
    h1 {
      font-size: 18px;
      margin: 0 0 12px;
    }
    h2 {
      font-size: 14px;
      margin: 0 0 10px;
    }
    h3 { font-size: 13px; margin: 16px 0 8px; }
    p { margin: 0 0 10px; }
    ul, ol { margin: 0 0 12px 20px; padding: 0; }
    li + li { margin-top: 4px; }
    code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 0.92em;
      background: #f4f4f4;
      border: 1px solid #e3e3e3;
      border-radius: 4px;
      padding: 0.08rem 0.22rem;
    }
    pre {
      margin: 12px 0;
      white-space: pre-wrap;
      overflow-wrap: anywhere;
      background: #f7f7f7;
      border: 1px solid #dcdcdc;
      border-radius: 8px;
      padding: 12px;
      break-inside: avoid;
    }
    pre code {
      border: 0;
      padding: 0;
      background: transparent;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0;
      font-size: 11px;
      break-inside: avoid;
    }
    th, td {
      border: 1px solid #d7d7d7;
      padding: 6px 8px;
      text-align: left;
      vertical-align: top;
    }
    th { background: #f5f5f5; font-weight: 700; }
    a { color: #111; text-decoration: underline; }
  </style>
</head>
<body>
  <div id="print-root"></div>
</body>
</html>`
}

function PrintableAnswerDocument({
  answerMarkdown,
  previousUserMessage,
  conversationUrl,
  timestampLabel,
}: PrintAnswerInput) {
  return (
    <main>
      <header>
        <div className="brand">BuildingRegs.app</div>
        {conversationUrl ? (
          <div className="meta">
            Conversation URL:{' '}
            <a href={conversationUrl} target="_blank" rel="noreferrer">
              {conversationUrl}
            </a>
          </div>
        ) : null}
        {timestampLabel ? <div className="meta">{timestampLabel}</div> : null}
      </header>

      <section>
        <h1>Question</h1>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {previousUserMessage}
        </ReactMarkdown>
      </section>

      <section>
        <h1>Answer</h1>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {answerMarkdown}
        </ReactMarkdown>
      </section>
    </main>
  )
}

type FeedbackDetailsDialogProps = {
  dialogRating: MessageFeedbackRating
  selectedTags: Array<MessageFeedbackReasonTag>
  note: string
  isSaving: boolean
  onDismiss: () => void
  onTagChange: (tag: MessageFeedbackReasonTag, isSelected: boolean) => void
  onNoteChange: (value: string) => void
  onSkip: () => void
  onSubmit: () => void
}

function FeedbackDetailsDialog({
  dialogRating,
  selectedTags,
  note,
  isSaving,
  onDismiss,
  onTagChange,
  onNoteChange,
  onSkip,
  onSubmit,
}: FeedbackDetailsDialogProps) {
  const isMobileDialog = useMediaQuery('(max-width: 640px)')
  const keyboardViewportInset = useKeyboardViewportInset()
  const feedbackKeyboardInset =
    isMobileDialog && keyboardViewportInset.isKeyboardOpen
      ? keyboardViewportInset.bottomInset
      : 0
  const feedbackDialogStyle = {
    '--feedback-keyboard-inset': `${feedbackKeyboardInset}px`,
    '--feedback-dialog-max-height': `calc(100dvh - ${feedbackKeyboardInset}px - env(safe-area-inset-bottom) - 1.5rem)`,
    maxHeight: 'var(--feedback-dialog-max-height)',
    ...(feedbackKeyboardInset > 0
      ? {
          marginBottom: `calc(${feedbackKeyboardInset}px + env(safe-area-inset-bottom))`,
        }
      : {}),
  } satisfies CSSProperties & Record<string, string>
  const reasonOptions = useMemo(
    () => getReasonOptions(dialogRating),
    [dialogRating],
  )

  return (
    <DialogContainer onDismiss={onDismiss}>
      <CustomDialog
        isDismissible
        size="S"
        aria-label="Answer feedback"
        UNSAFE_className="max-sm:!w-[calc(100vw-1.5rem)] max-sm:!max-w-[calc(100vw-1.5rem)] max-sm:!overflow-hidden max-sm:!rounded-2xl"
        UNSAFE_style={feedbackDialogStyle}
      >
        <div
          data-testid="message-feedback-dialog-layout"
          data-keyboard-open={feedbackKeyboardInset > 0 ? 'true' : 'false'}
          className="box-border flex max-h-[var(--feedback-dialog-max-height)] w-full max-w-full min-w-0 flex-col overflow-hidden sm:max-h-[calc(100dvh-3rem)]"
        >
          <div className="shrink-0 space-y-2 pb-3 text-left sm:pb-4">
            <h2 className="text-lg font-semibold text-neutral-950 dark:text-neutral-50 sm:text-xl">
              {dialogRating === 'positive'
                ? 'What went well?'
                : 'What went wrong?'}
            </h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Select anything that applies, or add a short note.
            </p>
          </div>

          <div className="min-h-0 flex-1 overscroll-contain overflow-y-auto overflow-x-hidden pr-1">
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {reasonOptions.map(({ tag, label }) => (
                <div key={tag} className="min-w-0 py-2.5 sm:py-3">
                  <Checkbox
                    isSelected={selectedTags.includes(tag)}
                    onChange={(isSelected) => onTagChange(tag, isSelected)}
                  >
                    <span className="block min-w-0 whitespace-normal break-words text-sm leading-snug">
                      {label}
                    </span>
                  </Checkbox>
                </div>
              ))}
            </div>

            <div className="min-w-0 max-w-full overflow-hidden pt-4 [&_*]:max-w-full [&_*]:min-w-0">
              <TextArea
                label="Anything else?"
                maxLength={2000}
                placeholder="Add a note, source, or correction..."
                rows={2}
                enterKeyHint="done"
                UNSAFE_className="w-full min-w-0 max-w-full "
                UNSAFE_style={{
                  width: '100%',
                  minWidth: 0,
                  maxWidth: '100%',
                }}
                value={note}
                onChange={onNoteChange}
              />
            </div>
          </div>

          <div className="mt-3 flex min-w-0 shrink-0 flex-row flex-nowrap items-center justify-end gap-2 overflow-hidden border-t border-neutral-100 bg-background pt-3 dark:border-neutral-800 sm:mt-4 sm:pt-4">
            <Button
              variant="secondary"
              fillStyle="outline"
              size="S"
              onPress={onSkip}
              isDisabled={isSaving}
              UNSAFE_className="shrink-0"
            >
              Skip
            </Button>
            <Button
              variant="primary"
              size="S"
              onPress={onSubmit}
              isDisabled={isSaving}
              UNSAFE_className="shrink-0"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Submit'
              )}
            </Button>
          </div>
        </div>
      </CustomDialog>
    </DialogContainer>
  )
}

export function MessageFeedbackControl({
  messageId,
  markdownContent,
  previousUserMessage,
  conversationUrl,
  timestampLabel,
  initialFeedback,
}: MessageFeedbackControlProps) {
  const [feedback, setFeedback] = useState<MessageFeedbackSummary | null>(
    initialFeedback ?? null,
  )
  const [dialogRating, setDialogRating] =
    useState<MessageFeedbackRating | null>(null)
  const [selectedTags, setSelectedTags] = useState<
    Array<MessageFeedbackReasonTag>
  >([])
  const [note, setNote] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [utilityStatus, setUtilityStatus] = useState<{
    kind: 'success' | 'error'
    message: string
  } | null>(null)
  const copyResetTimerRef = useRef<number | null>(null)
  const statusResetTimerRef = useRef<number | null>(null)
  const trimmedPreviousUserMessage = previousUserMessage?.trim() ?? ''
  const canPrint = trimmedPreviousUserMessage.length > 0

  useEffect(() => {
    setFeedback(initialFeedback ?? null)
  }, [initialFeedback])

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current !== null) {
        window.clearTimeout(copyResetTimerRef.current)
      }
      if (statusResetTimerRef.current !== null) {
        window.clearTimeout(statusResetTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (copyResetTimerRef.current !== null) {
      window.clearTimeout(copyResetTimerRef.current)
      copyResetTimerRef.current = null
    }
    if (statusResetTimerRef.current !== null) {
      window.clearTimeout(statusResetTimerRef.current)
      statusResetTimerRef.current = null
    }

    setDialogRating(null)
    setSelectedTags([])
    setNote('')
    setIsSaving(false)
    setIsCopying(false)
    setIsCopied(false)
    setIsPrinting(false)
    setError(null)
    setSavedMessage(null)
    setUtilityStatus(null)
  }, [messageId])

  const scheduleUtilityStatusReset = () => {
    if (statusResetTimerRef.current !== null) {
      window.clearTimeout(statusResetTimerRef.current)
    }
    statusResetTimerRef.current = window.setTimeout(() => {
      setUtilityStatus(null)
      statusResetTimerRef.current = null
    }, 2000)
  }

  const openDetails = (rating: MessageFeedbackRating) => {
    setDialogRating(rating)
    setSelectedTags(feedback?.rating === rating ? feedback.reasonTags : [])
    setNote(feedback?.rating === rating && feedback.note ? feedback.note : '')
  }

  const saveFeedback = async (
    rating: MessageFeedbackRating,
    reasonTags: Array<MessageFeedbackReasonTag>,
    nextNote: string | null,
    source: 'rating' | 'details',
  ) => {
    setIsSaving(true)
    setError(null)
    setUtilityStatus(null)
    try {
      const saved = await submitMessageFeedback({
        data: {
          messageId,
          rating,
          reasonTags,
          note: nextNote,
        },
      })
      setFeedback(saved)
      setSavedMessage('Thanks for the feedback.')
      trackAnalyticsEvent('answer_feedback_saved', {
        rating,
        source,
        reason_count: reasonTags.length,
        has_note: Boolean(nextNote?.trim()),
      })
      setIsSaving(false)
      return saved
    } catch {
      setError('Feedback could not be saved.')
      trackAnalyticsEvent('answer_feedback_save_failed', {
        rating,
        source,
        reason_count: reasonTags.length,
        has_note: Boolean(nextNote?.trim()),
      })
      setIsSaving(false)
      return null
    }
  }

  const handleRatingPress = async (rating: MessageFeedbackRating) => {
    const previousRating = feedback?.rating ?? null
    openDetails(rating)
    trackAnalyticsEvent('answer_feedback_details_opened', {
      rating,
      had_existing_feedback: Boolean(feedback),
      is_rating_change: previousRating !== rating,
    })
    if (previousRating !== rating) {
      await saveFeedback(rating, [], null, 'rating')
    }
  }

  const handleTagChange = (
    tag: MessageFeedbackReasonTag,
    isSelected: boolean,
  ) => {
    setSelectedTags((current) =>
      isSelected ? [...current, tag] : current.filter((item) => item !== tag),
    )
  }

  const handleSubmitDetails = async (rating: MessageFeedbackRating) => {
    const saved = await saveFeedback(
      rating,
      selectedTags,
      note || null,
      'details',
    )
    if (saved) setDialogRating(null)
  }

  const handleSkipDetails = () => {
    if (dialogRating) {
      trackAnalyticsEvent('answer_feedback_details_skipped', {
        rating: dialogRating,
        selected_reason_count: selectedTags.length,
        has_note: Boolean(note.trim()),
      })
    }
    setDialogRating(null)
  }

  const handleCopyAnswer = async () => {
    setIsCopying(true)
    setUtilityStatus(null)
    setError(null)

    try {
      await navigator.clipboard.writeText(markdownContent)
      setIsCopied(true)
      setUtilityStatus({ kind: 'success', message: 'Copied.' })
      scheduleUtilityStatusReset()
      trackAnalyticsEvent('answer_markdown_copied', {
        character_count: markdownContent.length,
      })

      if (copyResetTimerRef.current !== null) {
        window.clearTimeout(copyResetTimerRef.current)
      }
      copyResetTimerRef.current = window.setTimeout(() => {
        setIsCopied(false)
        copyResetTimerRef.current = null
      }, 2000)
    } catch {
      setUtilityStatus({ kind: 'error', message: 'Copy failed.' })
      scheduleUtilityStatusReset()
      trackAnalyticsEvent('answer_markdown_copy_failed', {
        character_count: markdownContent.length,
      })
    } finally {
      setIsCopying(false)
    }
  }

  const handlePrintAnswer = async () => {
    setIsPrinting(true)
    setUtilityStatus(null)
    setError(null)
    trackAnalyticsEvent('answer_print_requested', {
      has_conversation_url: Boolean(conversationUrl),
      has_timestamp: Boolean(timestampLabel),
    })

    let iframe: HTMLIFrameElement | null = null

    try {
      const [{ createRoot }, { flushSync }] = await Promise.all([
        import('react-dom/client'),
        import('react-dom'),
      ])

      iframe = document.createElement('iframe')
      iframe.setAttribute('aria-hidden', 'true')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = '0'
      document.body.appendChild(iframe)

      const printDocument = iframe.contentDocument
      const printWindow = iframe.contentWindow
      if (!printDocument || !printWindow) {
        throw new Error('Print frame unavailable')
      }

      printDocument.open()
      printDocument.write(getPrintDocumentShell())
      printDocument.close()

      const printRootElement = printDocument.getElementById('print-root')
      if (!printRootElement) {
        throw new Error('Print root unavailable')
      }

      const root = createRoot(printRootElement)
      let cleanupTimer: number | null = null
      const cleanup = () => {
        printWindow.removeEventListener('afterprint', cleanup)
        if (cleanupTimer !== null) window.clearTimeout(cleanupTimer)
        root.unmount()
        iframe?.remove()
      }

      flushSync(() => {
        root.render(
          <PrintableAnswerDocument
            answerMarkdown={markdownContent}
            previousUserMessage={trimmedPreviousUserMessage}
            conversationUrl={conversationUrl}
            timestampLabel={timestampLabel}
          />,
        )
      })

      printWindow.addEventListener('afterprint', cleanup)
      cleanupTimer = window.setTimeout(cleanup, 60_000)
      printWindow.focus()
      printWindow.print()
      setUtilityStatus({ kind: 'success', message: 'Print dialog opened.' })
      scheduleUtilityStatusReset()
    } catch {
      iframe?.remove()
      setUtilityStatus({ kind: 'error', message: 'Print failed.' })
      scheduleUtilityStatusReset()
    } finally {
      setIsPrinting(false)
    }
  }

  const visibleStatus =
    utilityStatus ??
    (error ? { kind: 'error' as const, message: error } : null) ??
    (savedMessage ? { kind: 'success' as const, message: savedMessage } : null)

  return (
    <div className="mt-2 flex w-full flex-col items-start gap-2 text-xs text-neutral-500 dark:text-neutral-400">
      <div
        role="group"
        aria-label="Answer actions"
        className="inline-flex items-center gap-0.5 rounded-full border border-neutral-200 bg-white/80 p-1 shadow-sm dark:border-neutral-800 dark:bg-neutral-950/70"
      >
        <ActionButton
          aria-label="Copy answer as Markdown"
          title="Copy answer as Markdown"
          size="S"
          isQuiet
          onPress={handleCopyAnswer}
          isDisabled={isCopying}
        >
          {isCopying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isCopied ? (
            <Check className="h-4 w-4" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </ActionButton>
        {canPrint ? (
          <ActionButton
            aria-label="Print answer"
            title="Print answer"
            size="S"
            isQuiet
            onPress={handlePrintAnswer}
            isDisabled={isPrinting}
          >
            {isPrinting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
          </ActionButton>
        ) : null}
        <span
          aria-hidden="true"
          className="mx-1 h-5 w-px bg-neutral-200 dark:bg-neutral-800"
        />
        <ActionButton
          aria-label="This answer was useful"
          aria-pressed={feedback?.rating === 'positive'}
          size="S"
          isQuiet
          onPress={() => handleRatingPress('positive')}
          isDisabled={isSaving}
        >
          {isSaving && dialogRating === 'positive' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ThumbsUp
              className={cn(
                'h-4 w-4',
                feedback?.rating === 'positive' &&
                  'fill-current text-neutral-950 dark:text-neutral-50',
              )}
            />
          )}
        </ActionButton>
        <ActionButton
          aria-label="This answer was not useful"
          aria-pressed={feedback?.rating === 'negative'}
          size="S"
          isQuiet
          onPress={() => handleRatingPress('negative')}
          isDisabled={isSaving}
        >
          {isSaving && dialogRating === 'negative' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ThumbsDown
              className={cn(
                'h-4 w-4',
                feedback?.rating === 'negative' &&
                  'fill-current text-neutral-950 dark:text-neutral-50',
              )}
            />
          )}
        </ActionButton>
      </div>
      {visibleStatus ? (
        <div className="px-1" aria-live="polite">
          <span
            className={cn(
              visibleStatus.kind === 'error'
                ? 'text-red-600 dark:text-red-400'
                : 'text-neutral-500 dark:text-neutral-400',
            )}
          >
            {visibleStatus.message}
          </span>
        </div>
      ) : null}

      {dialogRating ? (
        <FeedbackDetailsDialog
          dialogRating={dialogRating}
          selectedTags={selectedTags}
          note={note}
          isSaving={isSaving}
          onDismiss={() => setDialogRating(null)}
          onTagChange={handleTagChange}
          onNoteChange={setNote}
          onSkip={handleSkipDetails}
          onSubmit={() => handleSubmitDetails(dialogRating)}
        />
      ) : null}
    </div>
  )
}
